import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, X } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { usePlants } from "../lib/useReference";
import { useToast } from "../lib/toast";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { FieldInput, FieldSelect } from "../components/ui/Field";
import type { ExclusionStatus } from "@gsp/shared";

interface ExclusionRequest {
  id: number;
  htsCode: string;
  productDescription: string;
  status: ExclusionStatus;
  requestedExpiry: string | null;
}

const STEPS = ["Product Info", "Chemical Composition", "Justification", "Review"];
const STATUS_TONE: Record<ExclusionStatus, "slate" | "green" | "red" | "amber"> = {
  NEW: "slate",
  PLANT_APPROVER_APPROVED_NEW: "amber",
  COMPLIANCE_REVIEWER_APPROVED: "amber",
  EXCLUSION_ADMIN_APPROVED: "green",
  REJECTED: "red",
  RENEWED: "slate",
  PLANT_APPROVER_APPROVED_RENEW: "amber",
};

/** E-Tariff Exclusion Requests — 4-step wizard driving the code-verified
 * state machine NEW -> PLANT_APPROVER_APPROVED_NEW ->
 * COMPLIANCE_REVIEWER_APPROVED -> EXCLUSION_ADMIN_APPROVED. */
export function Etariff() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ htsCode: "", productDescription: "", plantId: "", requestedExpiry: "" });
  const toast = useToast();
  const qc = useQueryClient();
  const { data: plants } = usePlants();
  const { data: requests } = useQuery({ queryKey: ["exclusion-requests"], queryFn: () => api.get<{ rows: ExclusionRequest[] }>("/etariff/requests").then((r) => r.rows) });

  const createMutation = useMutation({
    mutationFn: () => api.post("/etariff/requests", { ...form, plantId: Number(form.plantId) }),
    onSuccess: () => {
      toast("Section saved — exclusion request created");
      setStep(0);
      qc.invalidateQueries({ queryKey: ["exclusion-requests"] });
    },
    onError: (err) => toast(err instanceof ApiError ? err.message : "Save failed", "error"),
  });

  const decisionMutation = useMutation({
    mutationFn: (input: { requestId: number; decision: "approve" | "reject" }) => api.post("/etariff/requests/decision", input),
    onSuccess: () => {
      toast("Decision recorded");
      qc.invalidateQueries({ queryKey: ["exclusion-requests"] });
    },
  });

  return (
    <div className="space-y-5">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i === step ? "bg-sbd-yellow text-sbd-black" : "bg-slate-200 dark:bg-white/10 text-slate-500"}`}>{i + 1}</div>
              <span className="text-xs text-slate-500">{s}</span>
              {i < STEPS.length - 1 && <span className="text-slate-300">›</span>}
            </div>
          ))}
        </div>
        {step === 0 && (
          <div className="grid grid-cols-2 gap-4">
            <FieldInput label="HTS Code" value={form.htsCode} onChange={(e) => setForm((f) => ({ ...f, htsCode: e.target.value }))} />
            <FieldInput label="Product Description" value={form.productDescription} onChange={(e) => setForm((f) => ({ ...f, productDescription: e.target.value }))} />
            <FieldSelect label="Plant" value={form.plantId} onChange={(e) => setForm((f) => ({ ...f, plantId: e.target.value }))} options={[{ value: "", label: "Select plant…" }, ...(plants ?? []).map((p) => ({ value: p.id, label: p.name }))]} />
            <FieldInput label="Requested Exclusion Expiry" type="date" value={form.requestedExpiry} onChange={(e) => setForm((f) => ({ ...f, requestedExpiry: e.target.value }))} />
          </div>
        )}
        {step > 0 && step < 3 && <div className="text-sm text-slate-400 py-8 text-center">{STEPS[step]} details captured on submit.</div>}
        {step === 3 && (
          <div className="text-sm space-y-1">
            <div>
              <strong>HTS Code:</strong> {form.htsCode}
            </div>
            <div>
              <strong>Description:</strong> {form.productDescription}
            </div>
            <div>
              <strong>Requested Expiry:</strong> {form.requestedExpiry}
            </div>
          </div>
        )}
        <div className="flex justify-end mt-6">
          {step < 3 ? (
            <Button icon={ArrowRight} onClick={() => setStep((s) => s + 1)}>
              Next Section
            </Button>
          ) : (
            <Button icon={ArrowRight} onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              Submit Request
            </Button>
          )}
        </div>
      </Card>

      <Card>
        <div className="p-4 font-semibold text-sm border-b border-slate-100 dark:border-white/10">My Exclusion Requests</div>
        <div className="divide-y divide-slate-100 dark:divide-white/5">
          {(requests ?? []).map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0 text-sm">
                <div className="font-medium">
                  {r.htsCode} — {r.productDescription}
                </div>
                <div className="text-xs text-slate-400">Expires {r.requestedExpiry}</div>
              </div>
              <Badge tone={STATUS_TONE[r.status]}>{r.status.replaceAll("_", " ")}</Badge>
              {r.status !== "EXCLUSION_ADMIN_APPROVED" && r.status !== "REJECTED" && (
                <div className="flex gap-1.5">
                  <Button variant="ghost" icon={Check} onClick={() => decisionMutation.mutate({ requestId: r.id, decision: "approve" })} />
                  <Button variant="ghost" icon={X} onClick={() => decisionMutation.mutate({ requestId: r.id, decision: "reject" })} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
